"use client";

import { ArrowLongRightIcon } from "@heroicons/react/24/outline";
import { signIn, useSession } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { AuthFormProps } from "./types";
import { attributionKey, getAdAttributionFromLocalStorage } from "@/app/ad-attribution";
import { classNames } from "@/helpers/tailwind-class";
import { webAssetsCdnBaseUrl } from "@/shared/constants";

const tagLines = [
  {
    text: "Money back guarantee after your first video (limit one).",
    icon: () => (
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_71_1139)">
          <path
            d="M1.5 6.75H15.75C15.9489 6.75 16.1397 6.82902 16.2803 6.96967C16.421 7.11032 16.5 7.30109 16.5 7.5V15C16.5 15.1989 16.421 15.3897 16.2803 15.5303C16.1397 15.671 15.9489 15.75 15.75 15.75H2.25C2.05109 15.75 1.86032 15.671 1.71967 15.5303C1.57902 15.3897 1.5 15.1989 1.5 15V6.75ZM2.25 2.25H13.5V5.25H1.5V3C1.5 2.80109 1.57902 2.61032 1.71967 2.46967C1.86032 2.32902 2.05109 2.25 2.25 2.25ZM11.25 10.5V12H13.5V10.5H11.25Z"
            fill="url(#paint0_linear_71_1139)"
          />
        </g>
        <defs>
          <linearGradient
            id="paint0_linear_71_1139"
            x1="9"
            y1="2.25"
            x2="9"
            y2="15.75"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#5161E8" />
            <stop offset="1" stopColor="#6848E8" />
          </linearGradient>
          <clipPath id="clip0_71_1139">
            <rect width="18" height="18" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
  },
  {
    text: "Founded in the USA. We respect your privacy.",

    icon: () => (
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_71_1136)">
          <path
            d="M9.75 7.5H15L8.25 17.25V10.5H3L9.75 0.75V7.5Z"
            fill="url(#paint0_linear_71_1136)"
          />
        </g>
        <defs>
          <linearGradient
            id="paint0_linear_71_1136"
            x1="9"
            y1="0.75"
            x2="9"
            y2="17.25"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#4D64E8" />
            <stop offset="1" stopColor="#6D42E7" />
          </linearGradient>
          <clipPath id="clip0_71_1136">
            <rect width="18" height="18" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
  },

  {
    text: "Get access to new AI models as they're released.",
    icon: () => (
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1.5 14.25H16.5V15.75H1.5V14.25ZM1.5 3.75L5.25 6L9 1.5L12.75 6L16.5 3.75V12.75H1.5V3.75Z"
          fill="url(#paint0_linear_111_41)"
        />
        <defs>
          <linearGradient
            id="paint0_linear_111_41"
            x1="9"
            y1="1.5"
            x2="9"
            y2="15.75"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#5360E8" />
            <stop offset="1" stopColor="#6849E8" />
          </linearGradient>
        </defs>
      </svg>
    ),
  },
];

function isInAppBrowser() {
  if (typeof navigator === "undefined") {
    return false;
  }

  if (navigator.userAgent.match(/FBAN|FBAV/i)) {
    return true;
  }

  if (navigator.userAgent.toLowerCase().includes("instagram")) {
    return true;
  }

  // @ts-expect-error checking for facebook in app browser
  if (typeof FB_IAB !== "undefined") {
    return true;
  }
  if (typeof document === "undefined") {
    return false;
  }
  if (document.documentElement.classList.contains("in-app-browser")) {
    return true;
  }

  return false;
}

export function Form({ callbackUrl = "/create", source, email: emailParam }: AuthFormProps) {
  const router = useRouter();
  const [inAppBrowser, setInAppBrowser] = useState(false);
  const [loading, setLoading] = useState<"google" | "email" | null>(null);
  const [email, setEmail] = useState(emailParam || "");
  const [error, setError] = useState<string | null>(null);

  const { status } = useSession();

  useEffect(() => {
    setInAppBrowser(isInAppBrowser());
  }, []);
  if (status === "authenticated") {
    router.push(callbackUrl || "/create");
  }

  const adAttribution = getAdAttributionFromLocalStorage();

  if (adAttribution) {
    if (callbackUrl.includes("?")) {
      callbackUrl = `${callbackUrl}&${attributionKey}=${adAttribution}`;
    } else {
      callbackUrl = `${callbackUrl}?${attributionKey}=${adAttribution}`;
    }
  }

  const handleSignIn = () => {
    setLoading("google");
    signIn("google", { redirect: true, callbackUrl });
  };

  const handleEmailSignIn = async () => {
    setLoading("email");

    signIn("email", { email, callbackUrl, redirect: false })
      .then((response) => {
        if (response?.error === "EmailSignin") {
          setError("There was an issue sending the email. Please try again.");
          setLoading(null);
          return null;
        }

        const params = new URLSearchParams();
        params.append("email", email);
        params.append("callbackUrl", callbackUrl);
        router.push(`/sign-in/email-check?${params.toString()}`);

        return null;
      })
      .catch((e) => console.error(e));
  };

  return (
    <div className="my-auto flex h-fit max-w-xs flex-col items-center justify-center">
      <div className="mb-2 block text-2xl text-white">Welcome to Magic Hour</div>
      <div className="text- mb-6 block text-neutral-50">
        Sign {source === "sign-up" ? "up" : "in"} to unlock all features
      </div>

      {!inAppBrowser && (
        <>
          <button
            type="button"
            disabled={loading === "google"}
            onClick={handleSignIn}
            className="group btn no-animation relative flex w-full items-center justify-center border-slate-700 bg-transparent hover:border-slate-600 hover:bg-transparent"
          >
            <div className="flex items-center justify-center">
              <img
                height={20}
                width={20}
                src={`${webAssetsCdnBaseUrl}/google.svg`}
                alt={"Google"}
                className="mr-4"
              />
              <span className="text-white">Continue with Google</span>
            </div>

            {loading === "google" ? (
              <div className="absolute right-4 ml-4 h-6 w-6 animate-spin rounded-[50%] border-2 border-slate-600 border-t-slate-400" />
            ) : (
              <ArrowLongRightIcon
                className="absolute right-4 ml-4 block h-6 w-6 translate-x-0 opacity-0 transition duration-300 group-hover:translate-x-1 group-hover:opacity-80"
                aria-hidden="true"
              />
            )}
          </button>

          <div className="my-4 flex w-full items-center gap-2">
            <div className="h-px flex-1 rounded-xl bg-linear-to-r from-teal-300/80 via-indigo-400/80 to-purple-600/80" />
            <div className="block px-4 text-slate-300">OR</div>
            <div className="h-px flex-1 rounded-xl bg-linear-to-r from-purple-600/80 via-indigo-400/80 to-teal-300/80" />
          </div>
        </>
      )}

      <form className="relative flex w-full flex-col gap-4">
        <div className="w-full">
          <input
            type={"email"}
            autoCapitalize="off"
            autoCorrect="off"
            onChange={(e) => setEmail(e.target.value)}
            value={email}
            required={true}
            minLength={1}
            disabled={loading === "email"}
            placeholder="Enter email"
            className="input input-bordered w-full bg-transparent"
          />
          {error && <span className="text-error left-0 text-xs">{error}</span>}
        </div>

        <button
          disabled={loading === "email"}
          formAction={handleEmailSignIn}
          className={classNames(
            "group btn btn-primary no-animation flex w-full animate-none items-center justify-center overflow-hidden text-white"
          )}
        >
          {loading === "email" ? (
            <div className="ml-4 h-6 w-6 animate-spin rounded-[50%] border-2 border-slate-600 border-t-slate-400" />
          ) : (
            <span>Continue</span>
          )}
        </button>
      </form>

      {source === "sign-up" && (
        <div className="mt-8 mr-auto text-left text-sm leading-5 text-slate-300">
          By registering, you agree to Magic Hour&apos;s{" "}
          <Link href="/terms-of-service" className="text-indigo-600 hover:text-indigo-500">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link href="/privacy-policy" className="text-indigo-600 hover:text-indigo-500">
            Privacy Policy
          </Link>
          .
        </div>
      )}

      <div className="mt-8 flex w-full flex-col gap-2">
        {tagLines.map((tagLine) => (
          <div key={tagLine.text} className="flex items-center gap-2 text-xs">
            <tagLine.icon />
            <span>{tagLine.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
